import * as Yup from 'yup'

import { useQueryString } from '../../../modules/navigation'
import useValidationSchema from './validationSchema'
import { type RegisterFormValues } from './RegisterForm.type'

/**
 * returns a filtered object with only valid data
 * this returns an object with "data" and "valid"
 * is all data is valid, "valid" will be true
 */
const filterInputs = (data: Yup.AnyObject, schema: Yup.AnyObjectSchema) => {
  if (Object.values(data).filter(d => !!d).length === 0) {
    return { data: {}, valid: false }
  }
  try {
    schema.validateSync(data, {
      abortEarly: false,
    })
    return { data, valid: true }
  } catch (error) {
    if (!(error instanceof Yup.ValidationError)) {
      return { data: {}, valid: false }
    }
    const invalidProperties = error.inner.map(property => property.path)
    const validData = Object.fromEntries(
      Object.entries(data)
        .filter(([key]) => !invalidProperties.includes(key)),
    )
    return { data: validData, valid: false }
  }
}

const getPhoneNumber = (input?: string) => {
  if (typeof input !== 'string' || !/^[0-9]{10,11}$/.test(input)) {
    return
  }
  if (input.length === 11) {
    if (input[0] !== '1') {
      return
    }
    if (/^18(00|33|44|55|66|77|88)[2-9]/.test(input)) {
      return input
    }
    return `+${input}`
  }
  return `+1${input}`
}

/**
 * returns default values to use for the register form
 * only valid data are returned
 * this returns an object with "data" and "valid"
 * is all data is valid, "valid" will be true
 */
export const useDefaultValues = (): { data: Partial<RegisterFormValues>, valid: boolean } => {
  const {
    customerFirstName: firstName,
    customerLastName: lastName,
    customerEmail: email,
    customerPhoneNumber: phoneNumber,
  } = useQueryString() ?? {}

  const validationSchema = useValidationSchema()

  const formattedPhoneNumber = getPhoneNumber(phoneNumber)

  return filterInputs({
    firstName,
    lastName,
    email,
    phoneNumber: formattedPhoneNumber ? { number: formattedPhoneNumber } : undefined,
  }, validationSchema)
}
